import { ChangeEvent, FC, FormEvent, useCallback, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiAlertCircle, FiInfo, FiMoon, FiSun } from "react-icons/fi";
import "./login.css";
import FootTopography from "./FootTopography";
import { Logo, curaFootLogoWhite } from "../../../assets";
import { AuthContext } from "../../../contexts";
import { api } from "../../../utils/api";
import { getLandingRoute } from "../../../utils/commonFunctions/accessScope";
import useColorScheme from "../../../hooks/useColorScheme";

const Login: FC = (): JSX.Element => {
	const navigate = useNavigate();
	const { setUser } = useContext(AuthContext);
	const { scheme, toggleScheme } = useColorScheme();
	const [credentialsPayload, setCredentialsPayload] = useState({
		username: "",
		password: ""
	});
	const [showPassword, setShowPassword] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState("");

	const toggleShowPassword = useCallback(() => {
		setShowPassword((current) => !current);
	}, []);

	const handleCredentialChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setCredentialsPayload((current) => Object.assign({}, current, { [name]: value }));
	}, []);

	const handleSubmit = useCallback(
		async (event: FormEvent) => {
			event.preventDefault();
			const { username, password } = credentialsPayload;
			if (!username || !password) {
				return setError("Enter both your username and password.");
			}
			setError("");
			setIsSubmitting(true);
			try {
				// The role is resolved from the account itself, server side.
				const response = await api.auth.login({ password, user_id: username });
				if (response) {
					localStorage.setItem("@user", response._id);
					setUser(response);
					navigate(getLandingRoute(response));
				}
			} catch {
				setError("We couldn't sign you in. Check your details and try again.");
			} finally {
				setIsSubmitting(false);
			}
		},
		[credentialsPayload, setUser, navigate]
	);

	return (
		<div className="cf-login">
			<section className="cf-login__art">
				<FootTopography scheme={scheme} />
				<div className="cf-art__overlay">
					{/* The white wordmark reads on the dark panel, the dark one on the light panel. */}
					<img
						className={`cf-brand cf-brand--${scheme}`}
						src={scheme === "dark" ? curaFootLogoWhite : Logo}
						alt="CuraFoot"
					/>
					<div>
						<p className="cf-art__headline">
							Fifty measurements per foot.
							<br />
							One device made for one person.
						</p>
						<hr className="cf-art__rule" />
						<p className="cf-art__meta">
							<b>50</b>
							<span>MEASURES / FOOT</span>
							<b>3D</b>
							<span>MESH + LANDMARKS</span>
						</p>
						<p className="cf-art__meta">
							<b>PDF</b>
							<span>CLINICAL REPORT</span>
						</p>
					</div>
				</div>
			</section>

			<section className="cf-login__panel">
				<button
					type="button"
					className="cf-scheme-toggle"
					onClick={toggleScheme}
					aria-label={scheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
					title={scheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
				>
					{scheme === "dark" ? <FiSun /> : <FiMoon />}
				</button>

				<form className="cf-form" onSubmit={handleSubmit} noValidate>
					<h1 className="cf-form__title">Sign in</h1>
					<p className="cf-form__subtitle">Clinician and franchise access to CuraFoot.</p>

					<div className="cf-field">
						<label className="cf-field__label" htmlFor="cf-username">
							Username
						</label>
						<div className="cf-field__control">
							<input
								id="cf-username"
								name="username"
								type="text"
								autoComplete="username"
								placeholder="you@curafoot.in"
								value={credentialsPayload.username}
								onChange={handleCredentialChange}
							/>
						</div>
					</div>

					<div className="cf-field">
						<label className="cf-field__label" htmlFor="cf-password">
							Password
						</label>
						<div className="cf-field__control">
							<input
								id="cf-password"
								name="password"
								type={showPassword ? "text" : "password"}
								autoComplete="current-password"
								placeholder="••••••••"
								value={credentialsPayload.password}
								onChange={handleCredentialChange}
							/>
							<button type="button" className="cf-field__reveal" onClick={toggleShowPassword}>
								{showPassword ? "HIDE" : "SHOW"}
							</button>
						</div>
					</div>

					<div className="cf-form__links">
						<Link to="/forget-password">Forgot password?</Link>
						<Link to="/forget-userid">Forgot username?</Link>
					</div>

					{error && (
						<p className="cf-form__error" role="alert">
							<FiAlertCircle />
							{error}
						</p>
					)}

					<button type="submit" className="cf-form__submit" disabled={isSubmitting}>
						{isSubmitting ? "Signing in…" : "Sign in"}
					</button>

					<hr className="cf-form__divider" />

					<p className="cf-form__notice">
						<FiInfo />
						If this is a shared clinic computer, sign out when you have finished.
					</p>

					<p className="cf-form__footer">CURAFOOT CRM</p>
				</form>
			</section>
		</div>
	);
};

export default Login;
